import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService, Player, PlayerStats } from '../services/api';

interface AwardCategory {
  name: string;
  key: keyof PlayerStats;
  icon: string;
  color: string;
}

const awardCategories: AwardCategory[] = [
  { name: '得分王', key: 'pts', icon: '🏀', color: '#e74c3c' },
  { name: '三分王', key: 'three_pm', icon: '🎯', color: '#f39c12' },
  { name: '籃板王', key: 'reb', icon: '📊', color: '#27ae60' },
  { name: '助攻王', key: 'ast', icon: '🤝', color: '#3498db' },
  { name: '封阻王', key: 'bs', icon: '🛡️', color: '#9b59b6' },
  { name: '抄截王', key: 'st', icon: '⚡', color: '#e67e22' }
];

interface LeaderData {
  player_id: string;
  player_name: string;
  team_name: string;
  team_abbreviation: string;
  value: number;
}

interface LeadersResponse {
  [category: string]: LeaderData[];
}

const Awards: React.FC = () => {
  const { year, group } = useParams<{ year: string; group: string }>();
  const [leadersData, setLeadersData] = useState<LeadersResponse>({});
  const [tournamentResults, setTournamentResults] = useState<any>(null);
  const [individualAwards, setIndividualAwards] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  useEffect(() => {
    const fetchData = async () => {
      if (!year || !group) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Get statistical leaders from the new API endpoint
        const leadersData = await apiService.getStatisticalLeaders(parseInt(year), group);
        setLeadersData(leadersData);
        
        // Get tournament results
        const tournamentData = await apiService.getTournamentResults(parseInt(year), group);
        setTournamentResults(tournamentData);
        
        // Get individual awards
        const individualAwardsData = await apiService.getIndividualAwards(parseInt(year), group);
        setIndividualAwards(individualAwardsData);
      } catch (err) {
        console.error('Error fetching awards data:', err);
        // Don't set error - just use empty data to show the table structure
        setLeadersData({});
        setTournamentResults(null);
        setIndividualAwards(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [year, group]);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getTopPlayers = (category: AwardCategory) => {
    const categoryName = category.name;
    return leadersData[categoryName] || [];
  };

  if (isLoading) return <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>;

  return (
    <div style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '0 20px 20px 20px',
      minHeight: '100vh',
      backgroundColor: '#e3f2fd'
    }}>
      {/* Navigation */}
      <div style={{ marginBottom: '40px', paddingTop: '20px' }}>
        <Link 
          to={`/summer-league/${year}/${group}`}
          style={{ 
            color: '#0066cc', 
            textDecoration: 'none', 
            fontSize: '16px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ← 返回
        </Link>
      </div>

      {/* Header */}
      <div style={{ 
        textAlign: 'center',
        marginBottom: '40px'
      }}>
        <h1 style={{ 
          fontSize: isDesktop ? '3rem' : '2rem', 
          color: '#2d3748',
          marginBottom: '12px',
          fontWeight: '700'
        }}>
          🏆 {year} 年 {group} 組 獎項
        </h1>
        <div style={{
          width: '60px',
          height: '4px',
          backgroundColor: '#3182ce',
          margin: '0 auto',
          borderRadius: '2px'
        }}></div>
      </div>

      {/* Team Awards */}
      {tournamentResults && (tournamentResults.champion || tournamentResults.runner_up || tournamentResults.third_place) && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: '3px solid #ffd700',
          marginBottom: '24px'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🏆</div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#ffd700', margin: 0 }}>
              團體獎項
            </h3>
          </div>
          
          <div style={{ minHeight: '120px' }}>
            {[
              { position: 1, title: '冠軍', medal: '🥇', color: '#ffd700', data: tournamentResults?.champion },
              { position: 2, title: '亞軍', medal: '🥈', color: '#c0c0c0', data: tournamentResults?.runner_up },
              { position: 3, title: '季軍', medal: '🥉', color: '#cd7f32', data: tournamentResults?.third_place }
            ].map((award) => (
              <div key={award.position} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: award.position < 3 ? '1px solid #e2e8f0' : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ fontSize: '1.5rem' }}>{award.medal}</div>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#2d3748' }}>
                      {award.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#718096' }}>
                      {award.data?.team?.name || '-'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

            {/* Individual Awards Grid */}
      {individualAwards && (individualAwards.best_coach || individualAwards.mvp || individualAwards.best_defensive) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : 'repeat(1, 1fr)',
          gap: '24px',
          marginBottom: '24px'
        }}>
          {[
            { type: 'best_coach', title: '最佳教練', icon: '👨‍💼', color: '#e74c3c', data: individualAwards?.best_coach },
            { type: 'mvp', title: '最有價值球員', icon: '👑', color: '#f39c12', data: individualAwards?.mvp },
            { type: 'best_defensive', title: '最佳防守球員', icon: '🛡️', color: '#27ae60', data: individualAwards?.best_defensive }
          ].map((award) => (
            <div key={award.type} style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: `3px solid ${award.color}`,
              textAlign: 'center',
              minHeight: '150px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              {/* Award Icon */}
              <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>
                {award.icon}
              </div>
              
              {/* Award Title */}
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: award.color,
                margin: '0 0 16px 0'
              }}>
                {award.title}
              </h3>
              
              {/* Award Winner */}
              <div style={{
                fontSize: '1rem',
                color: '#2d3748',
                fontWeight: '500',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {award.data ? (
                  award.type === 'best_coach' ? 
                    `${award.data.coach} (${award.data.team?.name || ''})` :
                    `${award.data.player?.name} (${award.data.team?.name || ''})`
                ) : '-'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Individual Awards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
        gap: '24px',
        marginBottom: '40px'
      }}>
        {awardCategories.map((category) => {
          const topPlayers = getTopPlayers(category);
          
          return (
            <div key={category.key} style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: `3px solid ${category.color}`,
              position: 'relative'
            }}>
              {/* Category Header */}
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                paddingBottom: '12px',
                borderBottom: `2px solid ${category.color}`,
                position: 'relative'
              }}>
                <div style={{
                  fontSize: '2rem',
                  marginBottom: '8px'
                }}>
                  {category.icon}
                </div>
                <h3 style={{
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  color: category.color,
                  margin: 0
                }}>
                  {category.name}
                </h3>
              </div>

                            {/* Top Players */}
              <div style={{ minHeight: '120px' }}>
                {topPlayers.length > 0 ? (
                  // Show actual player data
                  topPlayers.map((playerData, index) => (
                    <div key={playerData.player_id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: index < topPlayers.length - 1 ? '1px solid #e2e8f0' : 'none'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {/* Medal/Rank */}
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: 'white',
                          backgroundColor: index === 0 ? '#ffd700' : 
                                         index === 1 ? '#c0c0c0' : 
                                         index === 2 ? '#cd7f32' : '#6b7280'
                        }}>
                          {index + 1}
                        </div>
                        
                        {/* Player Info */}
                        <div>
                          <div style={{
                            fontWeight: 'bold',
                            fontSize: '14px',
                            color: '#2d3748'
                          }}>
                            {playerData.player_name}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#718096'
                          }}>
                            {playerData.team_abbreviation}
                          </div>
                        </div>
                      </div>
                      
                      {/* Value */}
                      <div style={{
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: category.color
                      }}>
                        {playerData.value}
                      </div>
                    </div>
                  ))
                ) : (
                  // Show empty numbered rows
                  Array.from({ length: 3 }, (_, index) => (
                    <div key={`empty-${index}`} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: index < 2 ? '1px solid #e2e8f0' : 'none'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {/* Medal/Rank */}
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: 'white',
                          backgroundColor: index === 0 ? '#ffd700' : 
                                         index === 1 ? '#c0c0c0' : 
                                         index === 2 ? '#cd7f32' : '#6b7280'
                        }}>
                          {index + 1}
                        </div>
                        
                        {/* Empty Player Info */}
                        <div>
                          <div style={{
                            fontWeight: 'bold',
                            fontSize: '14px',
                            color: '#a0aec0'
                          }}>
                            -
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#a0aec0'
                          }}>
                            -
                          </div>
                        </div>
                      </div>
                      
                      {/* Empty Value */}
                      <div style={{
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: '#a0aec0'
                      }}>
                        -
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default Awards; 